import { AbstractControl, ValidationErrors, Validators } from "@angular/forms";

/**
 * Símbolos que el backend acepta como "carácter especial". Es una copia literal
 * del `SYMBOL_REGEX` de `common/lib/domain/value-objects/identity/Password.validation.ts`:
 * toda la puntuación ASCII salvo comillas simples y dobles.
 *
 * Tiene que quedarse alineado con el backend — si el frontend acepta un símbolo
 * que el backend rechaza, el registro pasa la validación y el servidor lo tumba
 * después con INVALID_PASSWORD.
 */
export const PASSWORD_SYMBOL_REGEX = /[!#$%&()*+,\-./:;<=>?@[\\\]^_`{|}~]/;

export class DataValidations {

        static licensePlate = Validators.pattern('^[A-Za-z0-9]{5,7}$');
        static year = Validators.pattern('^[0-9]{4}$');
        static email = Validators.pattern('^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\\.[a-zA-Z0-9-.]+$');
        static phone = Validators.pattern('^\\+?[0-9]{1,15}$');

        static legalEntity = Validators.pattern(/.+/);
        static usoCFDI = Validators.pattern(/.+/);
        static noExterior = Validators.pattern(/[0-9]+/);
        static noInterior = Validators.pattern(/[0-9]+/);
        static address = Validators.pattern(/.+/);
        static city = Validators.pattern(/.+/);
        static state = Validators.pattern(/.+/);
        static country = Validators.pattern(/.+/);
        static caat = Validators.pattern(/^[A-Z0-9]{4,6}$/);

        static url = Validators.pattern(/^(https?:\/\/)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,24}(\/[^\s]*)?$/);


        static rfc = function (control: AbstractControl): ValidationErrors | null {
                let rfc = control.value
                if (!rfc) return null
                const rfcUpper = rfc.toUpperCase().trim()
                // Misma regla que el value object `RFC` del backend (patrón t_RFC del
                // SAT), la que aplican la edición y el timbrado: 3 letras (moral) o 4
                // (física), fecha AAMMDD válida y homoclave cuyo último carácter es un
                // dígito o la A.
                const rfcRegex = /^[A-ZÑ&]{3,4}\d{2}(0[1-9]|1[012])(0[1-9]|[12]\d|3[01])[A-Z\d]{2}[A\d]$/

                if (rfcRegex.test(rfcUpper)) {
                        return null;
                } else {
                        return { rfc: "No concuerda con los formatos para persona fisica o moral" };
                }
        }

        static zipcode = function (control: AbstractControl): ValidationErrors | null {
                let zipcode = control.value
                if (!zipcode) return null
                const zipcodeUpper = zipcode.toUpperCase().trim()
                const regex = /^\d{5}$/

                if (regex.test(zipcodeUpper)) {
                        return null;
                } else {
                        return { zipcode: "No concuerda con los formatos para zipcode" };
                }
        }

        static fullName = Validators.pattern(/^[A-Za-zÀ-ÿñÑçÇ 0-9]{2,128}$/);
        static companyName = Validators.pattern(/^[A-Za-zÀ-ÿñÑçÇ., 0-9]{2,128}$/);
        static password = function (control: AbstractControl): ValidationErrors | null {
                const password = control.value;
                if (!password) return null;

                const failed = [
                        password.length >= 8 || 'length',
                        /[A-Z]/.test(password) || 'upper',
                        /\d/.test(password) || 'digit',
                        PASSWORD_SYMBOL_REGEX.test(password) || 'symbol'
                ].filter(rule => rule !== true);

                return failed.length ? { password: failed } : null;
        }
}
/**
 * Contraseña temporal que el sistema asigna a un usuario creado por un admin.
 *
 * Tiene que cumplir la misma política que `Password.secure()` del backend
 * (mayúscula, número, símbolo, 8..72). Se construye tomando **un carácter
 * obligatorio de cada clase** y rellenando el resto al azar: sortear N
 * caracteres de una bolsa mezclada no garantiza nada — con la bolsa de 78 y 8
 * tiradas, casi la mitad de las contraseñas generadas salían sin dígito o sin
 * símbolo y el servidor las rechazaba con INVALID_PASSWORD, en un formulario
 * que ni siquiera pide contraseña.
 */
const PASSWORD_LOWER = 'abcdefghijkmnopqrstuvwxyz';
const PASSWORD_UPPER = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
const PASSWORD_DIGIT = '23456789';
// Subconjunto de PASSWORD_SYMBOL_REGEX, sin los que se escapan mal al pegarlos.
const PASSWORD_SYMBOL = '!#$%&*+-?@';
const PASSWORD_LENGTH = 12;

function randomInt(max: number): number {
        const crypto = typeof window !== 'undefined' ? window.crypto : undefined;
        if (!crypto?.getRandomValues) return Math.floor(Math.random() * max);

        // Rechazo por sesgo: 256 % max !== 0 deja los primeros valores más
        // probables que los últimos.
        const limit = Math.floor(256 / max) * max;
        const byte = new Uint8Array(1);
        do { crypto.getRandomValues(byte); } while (byte[0] >= limit);
        return byte[0] % max;
}

function pick(pool: string): string {
        return pool.charAt(randomInt(pool.length));
}

export function generateSecurePassword(length: number = PASSWORD_LENGTH): string {
        const all = PASSWORD_LOWER + PASSWORD_UPPER + PASSWORD_DIGIT + PASSWORD_SYMBOL;

        const chars = [
                pick(PASSWORD_LOWER),
                pick(PASSWORD_UPPER),
                pick(PASSWORD_DIGIT),
                pick(PASSWORD_SYMBOL)
        ];
        while (chars.length < Math.max(length, 8)) chars.push(pick(all));

        // Fisher-Yates: sin mezclar, las cuatro primeras posiciones delatan la
        // clase de cada carácter.
        for (let i = chars.length - 1; i > 0; i--) {
                const j = randomInt(i + 1);
                [chars[i], chars[j]] = [chars[j], chars[i]];
        }

        return chars.join('');
}
